import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import debounce from "lodash/debounce";

// Common Components Import
import ProductTableSales from '../../../Common/SalesMProduct';
import ChargesSection from "../../../Common/ChargesSection";
import { CHARGE_OPTIONS, UNIT_OPTIONS } from "../../../Common/constants";
import CommonPurchaseFields from "../../Purchase/CommonPurchaseFields";

import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";

const PRAMDCreate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [commonFieldsData, setcommonFieldsData] = useState({
        supplierinvoicedate: "",
        supplierinvoiceno: "",
        shipto: "",
        otherreference: "",
        deliverynoteno: "",
        deliverynotedate: "",
        supplier: null,
        supplierDetails: {
            contactperson: "",
            contactno: "",
            email: "",
        },
        supplierMultipleAddress: null,
        branch: null,
        warehouse: null,
    });

    // Product and charges state
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [rows, setRows] = useState([]);
    const [additionalCharges, setAdditionalCharges] = useState([
        {
            id: Date.now(),
            description: "",
            chargepercent: 0,
            amount: 0,
            gstPercent: 0,
            isPercentBased: true,
        },
    ]);

    const [purchaseinvoicedate, setPurchaseinvoicedate] = useState("");
    const [purchaseinvoiceno, setPurchaseinvoiceno] = useState("");
    const [purchasereturndate, setPurchasereturndate] = useState("");
    const [purchasereturnno, setPurchasereturnno] = useState("");
    const [jobnumber, setjobnumber] = useState("");
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState("");
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);

    // Validate purchase return number with 2 decimal places handling
    const checkPurchasereturnNo = debounce(async (number) => {
        if (!number) {
            setEnquiryValidationMsg("");
            setIsEnquiryValid(false);
            return;
        }

        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg("❌ Only alphanumeric characters and hyphens are allowed");
            setIsEnquiryValid(false);
            return;
        }

        try {
            const response = await axios.get(`${BASE_URL}purchaseReturn/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg("❌ This purchase return number already exists.");
                setIsEnquiryValid(false);
            } else {
                setEnquiryValidationMsg("✅ This purchase return number is available.");
                setIsEnquiryValid(true);
            }
        } catch (error) {
            setEnquiryValidationMsg("❌ Error validating purchase return number.");
            setIsEnquiryValid(false);
        }
    }, 400);

    useEffect(() => {
        if (purchasereturnno) {
            checkPurchasereturnNo(purchasereturnno);
        }
    }, [purchasereturnno]);


    // Fetch product data
    const getPumpProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/1/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );
            const products = response.data.Index || response.data.data || [];
            setProductOptions(
                products.map((product) => ({
                    value: product.id,
                    label: `${product.description} - ${product.moc} - ${product.size} - ${product.tagnumber} - ${product.serialnumber} - ${product.partno}`,
                    description: product.description,
                    uom: product.unitofmeasurement || UNIT_OPTIONS[0].value,
                    igst: product.igst || 0,
                    partno: product.partno,
                    serialnumber: product.serialnumber,
                    modelnumber: product.modelnumber,
                    tagnumber: product.tagnumber,
                    hsnsacno: product.hsnsacno,
                    moc: product.moc || "",
                    type: product.type || "",
                    size: product.size || "",
                    rating: product.rating || "",
                    classes: product.classes || "",
                    schedule: product.schedule || "",
                    certification: product.certification || "",
                }))
            );
        } catch (error) {
            Swal.fire("Error", "Failed to fetch product data", "error");
        }
    };

    useEffect(() => {
        getPumpProductData();
    }, [productSearch]);

    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
    }, 500);

    // Handle product selection with 2 decimal places
    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
        const selectedProductData = productOptions.find(
            (product) => product.value === selectedOption.value
        );

        if (selectedProductData) {
            const newRow = {
                description: selectedProductData.description,
                value: selectedProductData.value,
                label: selectedProductData.label,
                productremarks: "",
                quantity: "",
                uom: selectedProductData.uom || "",
                unitRateInr: parseFloat((selectedProductData.unitrateinr || 0).toFixed(2)),
                gstPercent: parseFloat((selectedProductData.igst || 0).toFixed(2)),
                amount: parseFloat((selectedProductData.unitrateinr || 0).toFixed(2)),
                totalinr: parseFloat(((selectedProductData.unitrateinr || 0) * (1 + (selectedProductData.igst || 0) / 100)).toFixed(2)),
                hsnsacno: selectedProductData.hsnsacno,
                partno: selectedProductData.partno,
                serialnumber: selectedProductData.serialnumber,
                moc: selectedProductData.moc || "",
                type: selectedProductData.type || "",
                size: selectedProductData.size || "",
                rating: selectedProductData.rating || "",
                classes: selectedProductData.classes || "",
                schedule: selectedProductData.schedule || "",
                certification: selectedProductData.certification || "",
            };
            setRows([...rows, newRow]);
        }
    };

    // Handle row changes with 2 decimal places
    const handleRowChange = (index, key, value) => {
        const updatedRows = [...rows];
        updatedRows[index][key] = value;

        if (["quantity", "unitRateInr", "gstPercent"].includes(key)) {
            const quantity = parseFloat(updatedRows[index].quantity) || 0;
            const unitRateInr = parseFloat(updatedRows[index].unitRateInr) || 0;
            const gstPercent = parseFloat(updatedRows[index].gstPercent) || 0;

            const amount = parseFloat((quantity * unitRateInr).toFixed(2));
            const gstAmount = parseFloat(((amount * gstPercent) / 100).toFixed(2));
            const totalinr = parseFloat((amount + gstAmount).toFixed(2));

            updatedRows[index].amount = amount;
            updatedRows[index].totalinr = totalinr;
        }

        setRows(updatedRows);
    };

    // Create empty charge row
    const createEmptyCharge = () => ({
        id: Date.now(),
        description: "",
        chargepercent: 0,
        amount: 0,
        gstPercent: 0,
        isPercentBased: true,
    });

    // Handle charge changes with 2 decimal places
    const handleChargeChange = (index, field, value, isBlur = false) => {
        setAdditionalCharges(prev => {
            const newCharges = [...prev];
            const charge = { ...newCharges[index] };
            const netTotal = rows.reduce((sum, row) => sum + (row.totalinr || 0), 0);

            if (field === "chargepercent") {
                const percentage = Math.min(value, 100);
                charge.chargepercent = parseFloat(percentage.toFixed(2));

                if (isBlur || percentage === 100) {
                    const calculatedAmount = (percentage * netTotal) / 100;
                    charge.amount = parseFloat(calculatedAmount.toFixed(2));
                } else {
                    charge.amount = 0;
                }
            }
            else if (field === "amount") {
                charge.amount = parseFloat(value.toFixed(2));
                charge.chargepercent = 0;
            }
            else if (field === "gstPercent") {
                charge.gstPercent = parseFloat(value.toFixed(2));
            }
            else {
                charge[field] = value;
            }

            newCharges[index] = charge;
            return newCharges;
        });
    };

    // Add new charge row
    const addNewChargeRow = () => {
        setAdditionalCharges((prev) => [...prev, createEmptyCharge()]);
    };

    // Remove charge row
    const removeChargeRow = (index) => {
        if (additionalCharges.length > 1) {
            setAdditionalCharges((prev) => prev.filter((_, i) => i !== index));
        }
    };

    // Remove product row
    const removeRow = (index) => {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    };

    // Calculate products total with 2 decimal places
    const productsTotal = useMemo(() => {
        const total = rows.reduce((total, row) => {
            const rowTotal = (row.quantity || 0) * (row.unitRateInr || 0) * (1 + (row.gstPercent || 0) / 100);
            return total + rowTotal;
        }, 0);
        return parseFloat(total.toFixed(2));
    }, [rows]);

    useEffect(() => {
        // Recalculate charge amounts based on percentages when productsTotal changes
        setAdditionalCharges(prevCharges => {
            return prevCharges.map(charge => {
                if (charge.chargepercent > 0) {
                    return {
                        ...charge,
                        amount: parseFloat(((charge.chargepercent * productsTotal) / 100).toFixed(2))
                    };
                }
                return charge;
            });
        });
    }, [productsTotal]);

    // Calculate charges total with 2 decimal places
    const additionalChargesTotal = useMemo(() => {
        const total = additionalCharges.reduce((total, charge) => {
            const chargeTotal = (charge.amount || 0) * (1 + (charge.gstPercent || 0) / 100);
            return total + chargeTotal;
        }, 0);
        return parseFloat(total.toFixed(2));
    }, [additionalCharges]);

    // Calculate gross total and round to nearest integer with 2 decimal places
    const grossTotal = parseFloat((productsTotal + additionalChargesTotal).toFixed(2));
    const roundedGrossTotal = Math.round(grossTotal);
    const grandTotal = roundedGrossTotal;

    // Form validation
    const validateForm = () => {
        if (!isEnquiryValid) {
            Swal.fire("Error", "Please enter a valid purchase return number", "error");
            return false;
        }
        if (!commonFieldsData.supplier) {
            Swal.fire("Error", "Please select a supplier", "error");
            return false;
        }
        if (!commonFieldsData.supplierMultipleAddress) {
            Swal.fire("Error", "Please select supplier address", "error");
            return false;
        }
        if (rows.length === 0) {
            Swal.fire("Error", "Please add at least one product", "error");
            return false;
        }
        return true;
    };

    // Handle form submission with 2 decimal places
    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);

        // Prepare purchase return items with 2 decimal places
        const purchaseReturnItem = rows.map((row) => ({
            product: { id: row.value },
            quantity: row.quantity,
            uom: row.uom,
            unitrateinr: parseFloat(row.unitRateInr.toFixed(2)),
            gst: parseFloat(row.gstPercent.toFixed(2)),
            amount: parseFloat(row.amount.toFixed(2)),
            totalinr: parseFloat(row.totalinr.toFixed(2)),
            productremarks: row.productremarks,
            partno: row.partno || "",
            hsnsacno: row.hsnsacno || "",
            serialnumber: row.serialnumber || "",
            moc: row.moc || "",
            type: row.type || "",
            size: row.size || "",
            rating: row.rating || "",
            classes: row.classes || "",
            schedule: row.schedule || "",
            certification: row.certification || "",
        }));

        // Prepare additional charges with 2 decimal places
        const additionalChargesData = additionalCharges
            .filter((charge) => charge.description && (charge.amount > 0 || charge.chargepercent > 0))
            .map((charge) => {
                const chargeAmount = parseFloat(charge.amount.toFixed(2));
                const gstPercent = parseFloat(charge.gstPercent.toFixed(2));
                const gstAmount = parseFloat((chargeAmount * gstPercent / 100).toFixed(2));
                const chargetotal = parseFloat((chargeAmount + gstAmount).toFixed(2));

                return {
                    chargetype: charge.description,
                    chargepercent: parseFloat(charge.chargepercent.toFixed(2)),
                    chargeamount: chargeAmount,
                    chargegst: gstPercent,
                    chargetotal: chargetotal,
                    isPercentBased: charge.isPercentBased,
                };
            });

        // Calculate totals with 2 decimal places
        const additionalChargesTotalCalc = parseFloat(additionalChargesData.reduce(
            (sum, charge) => sum + charge.chargetotal,
            0
        ).toFixed(2));

        const productsTotalCalc = parseFloat(purchaseReturnItem.reduce(
            (sum, item) => sum + item.totalinr,
            0
        ).toFixed(2));

        const grossTotalCalc = parseFloat((productsTotalCalc + additionalChargesTotalCalc).toFixed(2));

        // Prepare payload
        const payload = {
            purchaseReturnItem: purchaseReturnItem,
            additionalCharges: additionalChargesData.length > 0 ? additionalChargesData : null,
            nettotalproduct: productsTotalCalc,
            additionalchargestotal: additionalChargesTotalCalc,
            grosstotal: grossTotalCalc,
            grandtotalproduct: grossTotalCalc,
            purchasereturntype: "Manufacturing",
            purchasereturncategory: "Domestic",
            purchaseinvoiceno,
            purchaseinvoicedate,
            purchasereturnno,
            purchasereturndate,
            jobnumber,
            supplierinvoicedate: commonFieldsData.supplierinvoicedate,
            supplierinvoiceno: commonFieldsData.supplierinvoiceno,
            shipto: commonFieldsData.shipto,
            otherreference: commonFieldsData.otherreference,
            deliverynoteno: commonFieldsData.deliverynoteno,
            deliverynotedate: commonFieldsData.deliverynotedate,
            supplier: commonFieldsData.supplier ? { id: commonFieldsData.supplier.value } : null,
            contactperson: commonFieldsData.supplierDetails?.contactperson,
            contactno: commonFieldsData.supplierDetails?.contactno,
            email: commonFieldsData.supplierDetails?.email,
            supplierMultipleAddress: commonFieldsData.supplierMultipleAddress
                ? { id: commonFieldsData.supplierMultipleAddress.value }
                : null,
            branch: commonFieldsData.branch ? { id: commonFieldsData.branch.value } : null,
            warehouse: commonFieldsData.warehouse ? { id: commonFieldsData.warehouse.value } : null,
        };

        try {
            const response = await axios.post(`${BASE_URL}purchaseReturn/save`, payload, {
                headers: authHeader(),
            });
            Swal.fire("Success", "Purchase Return created successfully", "success");
            navigate("/prindex/1");
        } catch (error) {
            console.error("Submission error:", error);
            Swal.fire(
                "Error",
                error.response?.data?.message ||
                error.response?.data ||
                "Failed to create purchase return",
                "error"
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleCommonDataChange = (data) => {
        setcommonFieldsData((prev) => ({
            ...prev,
            ...data,
        }));
    };

    return (
        <div className="container-fluid">
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Purchase Return No *</Form.Label>
                            <Form.Control
                                type="text"
                                value={purchasereturnno}
                                onChange={(e) => setPurchasereturnno(e.target.value)}
                                isInvalid={!!enquiryValidationMsg.includes("❌")}
                                isValid={!!enquiryValidationMsg.includes("✅")}
                            />
                            <Form.Control.Feedback
                                type={enquiryValidationMsg.includes("✅") ? "valid" : "invalid"}
                            >
                                {enquiryValidationMsg}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Purchase Return Date *</Form.Label>
                            <Form.Control
                                type="date"
                                value={purchasereturndate}
                                onChange={(e) => setPurchasereturndate(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Purchase Invoice No</Form.Label>
                            <Form.Control
                                type="text"
                                value={purchaseinvoiceno}
                                onChange={(e) => setPurchaseinvoiceno(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Purchase Invoice Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={purchaseinvoicedate}
                                onChange={(e) => setPurchaseinvoicedate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Job Number</Form.Label>
                            <Form.Control
                                type="text"
                                value={jobnumber}
                                onChange={(e) => setjobnumber(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <CommonPurchaseFields
                    initialData={commonFieldsData}
                    onDataChange={handleCommonDataChange}
                />

                {/* Products Section */}
                <div className="mt-4">
                    <h5>Products</h5>
                    <Row>
                        <Col md="12">
                            <Form.Group>
                                <Form.Label>Select Product</Form.Label>
                                <Select
                                    value={selectedProduct}
                                    onChange={handleProductChange}
                                    options={productOptions}
                                    placeholder="Search and select product"
                                    onInputChange={handleProductSearchChange}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Common ProductTable Component */}
                    <ProductTableSales
                        rows={rows}
                        unitOptions={UNIT_OPTIONS}
                        onRowChange={handleRowChange}
                        onRemoveRow={removeRow}
                    />
                </div>

                {/* Charges Section */}
                <ChargesSection
                    additionalCharges={additionalCharges}
                    chargeOptions={CHARGE_OPTIONS}
                    productsTotal={productsTotal}
                    additionalChargesTotal={additionalChargesTotal}
                    grossTotal={grossTotal}
                    grandTotal={grandTotal}
                    onChargeChange={handleChargeChange}
                    onAddCharge={addNewChargeRow}
                    onRemoveCharge={removeChargeRow}
                />

                <div className="d-flex justify-content-end mt-4">
                    <button
                        type="button"
                        className="btn btn-primary me-2"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" />
                                <span className="ms-2">Submitting...</span>
                            </>
                        ) : (
                            "Submit"
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default PRAMDCreate;